import { Component, ElementRef, ViewChild } from '@angular/core';
import { HeaderLogoComponent } from "../header/widgets/header-logo/header-logo.component";
import { FeatherIconComponent } from "../ui/feather-icon/feather-icon.component";
import { items, menuItems } from '../../data/menu';
import { SvgIconComponent } from "../ui/svg-icon/svg-icon.component";
import { CommonModule } from '@angular/common';
import { Menu } from '../../interface/menu';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { LayoutService } from '../../services/layout.service';
import { TranslatePipe } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { PermissionService } from '../../services/permission.service';

@Component({
  selector: 'app-sidebar',
  imports: [CommonModule, RouterModule, HeaderLogoComponent,
            FeatherIconComponent, SvgIconComponent, TranslatePipe],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss'
})

export class SidebarComponent {

   public menuItems: Menu[] = [];
  public originalMenuItems = menuItems;
  public items = items;
  public leftArrow: boolean = false;
  public rightArrow: boolean = true;
  public pinedItem: Menu[] = [];
 
  private routerSubscription: Subscription;
  private itemsSubscription: Subscription;
  private permissionsSubscription: Subscription;
 
  constructor(
    private router: Router,
    public layoutService: LayoutService,
    private permissionService: PermissionService
  ) {
 
    this.routerSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.updateActiveMenu(event.url);
      }
    });
  }

  openExternalLink(url: string | undefined): void {
    if (!url) {
      return;
    }

    const userToken = localStorage.getItem('token') || '';
    const encoded = btoa(userToken);
    const currentUrl = window.location.href;
    const externalUrl = `${url}#token=${encoded}&sourceUrl=${encodeURIComponent(currentUrl)}`;

    window.open(externalUrl, '_blank', 'noopener,noreferrer');

    // Stay on current AeroVera page — keep sidebar active for the current route only
    // (do not mark external User Management as active)
    this.updateActiveMenu(this.router.url);
  }


 
  ngOnInit() {
    // Menu items subscription
    this.itemsSubscription = this.items.subscribe(menuItems => {
      this.originalMenuItems = menuItems;
      this.filterMenuBasedOnPermissions();
    });
 
    // Subscribe to permissions changes
    this.permissionsSubscription = this.permissionService.permissions$.subscribe(
      () => {
        this.filterMenuBasedOnPermissions();
      }
    );
  }
 
  ngOnDestroy() {
    // All subscriptions unsubscribe handled here
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
    if (this.itemsSubscription) {
      this.itemsSubscription.unsubscribe();
    }
    if (this.permissionsSubscription) {
      this.permissionsSubscription.unsubscribe();
    }
  }
 
  filterMenuBasedOnPermissions(): void {
    
    console.log('Filtering menu based on permissions');
    const allowed = this.permissionService.getAllowedRoutesForMenu();
 
    if (allowed.length === 0) {
      this.menuItems = [];
      console.log('No permissions - Empty menu');
      return;
    }
    // debugger
 
    // Filter menu items based on permissions
    this.menuItems = this.filterMenuItems(this.originalMenuItems, allowed);
    this.menuItems = this.sortMenuByPermissions(this.menuItems, allowed);
 
    console.log('Filtered menu items count:', this.menuItems);
 
    this.updateActiveMenu(this.router.url);
  }
 
  // FILTER RECURSIVE METHOD
  filterMenuItems(menuList: Menu[], allowed: any[]): Menu[] {
  return menuList
    .map(menu => {
      const menuPath = this.permissionService.normalizePath(menu.path || "");
      
      // If the menu has children, recursively filter them as well
      if (menu.children && menu.children.length > 0) {
        const filteredChildren = this.filterMenuItems(menu.children, allowed);

        if (filteredChildren.length > 0) {
          return { ...menu, children: filteredChildren };
        }
        return null;
      }

      // Normalize the allowed route for comparison
      const isAllowed = allowed.some(a => this.permissionService.normalizePath(a.route) === menuPath);

      return isAllowed ? menu : null;
    })
    .filter(x => x !== null) as Menu[];
}

 
  //Sort method with proper parent ordering
  sortMenuByPermissions(menuItems: Menu[], allowed: any[]): Menu[] {
    return menuItems
      .map(menu => {
        const menuPath = this.permissionService.normalizePath(menu.path || "");
 
        //Parent ka khud ka SortOrder find karo (not from children)
        const parentOrder = allowed.find(x => x.route === menuPath)?.sortOrder || 999;
 
        if (menu.children && menu.children.length > 0) {
          // Children ko sort karo
          menu.children = menu.children.sort((a, b) => {
            const aOrder = allowed.find(x => x.route === this.permissionService.normalizePath(a.path || ""))?.sortOrder || 999;
            const bOrder = allowed.find(x => x.route === this.permissionService.normalizePath(b.path || ""))?.sortOrder || 999;
            return aOrder - bOrder;
          });
 
          return { ...menu, sortOrder: parentOrder }; // Use parent's own order
        }
 
        // use its own order
        return { ...menu, sortOrder: parentOrder };
      })
      .sort((a, b) => (a.sortOrder || 999) - (b.sortOrder || 999));
  }
 
 
  updateActiveMenu(url: string): void {
    const cleanUrl = url.split('?')[0].split('#')[0];

    // Reset all active flags first (prevents Dashboard + User Management both active)
    this.clearActiveState(this.menuItems);

    this.menuItems.forEach(menuItem => {
      // External links (Control Hub, etc.) must never be "active" in-app
      if (this.isExternalPath(menuItem.path)) {
        return;
      }

      if (menuItem.path === cleanUrl) {
        this.setNavActive(menuItem);
      }

      if (menuItem.children) {
        menuItem.children.forEach(subItem => {
          if (this.isExternalPath(subItem.path)) {
            return;
          }

          if (subItem.path === cleanUrl) {
            this.setNavActive(subItem);
          }

          if (subItem.children) {
            subItem.children.forEach(subSubItem => {
              if (this.isExternalPath(subSubItem.path)) {
                return;
              }

              if (subSubItem.path === cleanUrl) {
                this.setNavActive(subSubItem);
              }
            });
          }
        });
      }
    });
  }

  private clearActiveState(items: Menu[]): void {
    items.forEach(item => {
      item.active = false;
      if (item.children?.length) {
        this.clearActiveState(item.children);
      }
    });
  }

  private isExternalPath(path?: string): boolean {
    return !!path && (path.startsWith('http://') || path.startsWith('https://'));
  }
 
  setNavActive(item: Menu) {
    this.menuItems.forEach(menuItem => {
      if (menuItem !== item) {
        menuItem.active = false;
      }
      if (menuItem.children && menuItem.children.includes(item)) {
        menuItem.active = true;
      }
      if (menuItem.children) {
        menuItem.children.forEach(submenuItems => {
          if (submenuItems.children && submenuItems.children.includes(item)) {
            menuItem.active = true;
            submenuItems.active = true;
          }
        });
      }
    });
    item.active = true;
  }
 
  toggleMenu(item: Menu) {
    if (!item.active) {
      this.menuItems.forEach((menu) => {
        if (this.menuItems.includes(item)) {
          menu.active = false
        }
        if (!menu.children) {
          return false;
        }
 
        menu.children.forEach((subMenu) => {
          if (menu.children?.includes(item)) {
            subMenu.active = false
          }
 
          if (subMenu.children) {
            subMenu.children.forEach((details) => {
              if (subMenu.children?.includes(item)) {
                details.active = false;
              }
            })
          }
        })
      })
    }
    item.active = !item.active;
  }
 
  scrollLeft() {
    this.rightArrow = true;
    if (this.layoutService.margin != 0) {
      this.layoutService.margin = this.layoutService.margin + 500;
    }
 
    if (this.layoutService.margin == 0) {
      this.leftArrow = false;
    }
  }
 
  scrollRight() {
    this.leftArrow = true;
    if (this.layoutService.margin != -4500) {
      this.layoutService.margin = this.layoutService.margin - 500;
    }
    if (this.layoutService.margin == -4500) {
      this.rightArrow = false;
    }
  }
 
  closeSidebar() {
    this.layoutService.closeSidebar = true;
  }
 
  pined(item: Menu) {
    if (!item.pined) {
      this.menuItems.filter((details) => {
        if (details.title) {
          if (this.menuItems.includes(item)) {
            item.pined = true;
            if (!this.pinedItem.includes(item)) {
              this.pinedItem.push(item);
            }
          }
        }
      })
    } else {
      item.pined = false;
      this.pinedItem.splice(this.pinedItem.indexOf(item), 1)
    }
 
    this.scroll(item)
  }
 
  scroll(item: Menu) {
    if (item && item.title) {
      const scrollDiv = document.getElementById(item.title);
 
      if (scrollDiv) {
        setTimeout(() => {
          scrollDiv.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }, 100);
      }
    }
  }
}
